
import React, { useState, useEffect, useCallback, useMemo } from "react";
import AdminHeader from "../components/AdminHeader";
import { Link } from "react-router-dom";
import {
  getUsersWithMessages,
  type UsersWithMessagesSort,
} from "../../../api/rest-apis";
import type { AdminMessageUser } from "../../../interfaces/rest-api";
import { getMembershipBadgeMeta } from "../../../helpers/membershipBadge";
import { AdvancedFilterModal } from "../email-campaign/components/AdvancedFilterModal";
import type {
  CampaignUserFilterKey,
  CampaignUserFilters,
} from "../email-campaign/types";
import {
  CharacterLibraryProvider,
  useCharacterLibrary,
} from "../email-campaign/CharacterLibraryProvider";

type MessageUser = AdminMessageUser;

const SORT_OPTIONS: Array<{
  value: UsersWithMessagesSort;
  label: string;
}> = [
  { value: "latest", label: "Latest Activity" },
  { value: "tokens", label: "Most Conversations" },
];

const FILTER_TOGGLES: Array<{
  key: CampaignUserFilterKey;
  label: string;
}> = [
  { key: "paidUsersOnly", label: "Paid Users" },
  { key: "ranOutOfTokensOnly", label: "Ever Ran Out of Tokens" },
];

const formatDateOnly = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const MessagesFC: React.FC = () => {
  const [users, setUsers] = useState<MessageUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] =
    useState<UsersWithMessagesSort>(SORT_OPTIONS[0].value);
  const [userFilters, setUserFilters] = useState<CampaignUserFilters>({
    sort: "latest",
    paidUsersOnly: false,
    ranOutOfTokensOnly: false,
    minTokens: null,
    maxTokens: null,
    fromDate: null,
    toDate: null,
    maxConversationCount: null,
    minUniqueCharacters: null,
    requiredCharacterIds: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdvancedFilterModalOpen, setIsAdvancedFilterModalOpen] =
    useState(false);
  const { characters } = useCharacterLibrary();

  const characterNameById = useMemo(() => {
    return characters.reduce<Record<string, string>>(
      (accumulator, character) => {
        accumulator[character.id] = character.name;
        return accumulator;
      },
      {}
    );
  }, [characters]);

  const advancedFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (typeof userFilters.minTokens === "number") {
      parts.push(`≥ ${userFilters.minTokens.toLocaleString()} tokens`);
    }
    if (typeof userFilters.maxTokens === "number") {
      parts.push(`≤ ${userFilters.maxTokens.toLocaleString()} tokens`);
    }
    if (userFilters.fromDate) {
      parts.push(`from ${formatDateOnly(userFilters.fromDate)}`);
    }
    if (userFilters.toDate) {
      parts.push(`to ${formatDateOnly(userFilters.toDate)}`);
    }
    if (typeof userFilters.maxConversationCount === "number") {
      parts.push(`≤ ${userFilters.maxConversationCount} chats`);
    }
    if (typeof userFilters.minUniqueCharacters === "number") {
      parts.push(`with ≥ ${userFilters.minUniqueCharacters} characters`);
    }
    if (userFilters.requiredCharacterIds?.length) {
      const resolvedNames = userFilters.requiredCharacterIds.map(
        (id) => characterNameById[id] ?? id
      );
      parts.push(`must include ${resolvedNames.join(", ")}`);
    }
    return parts.join(" · ");
  }, [userFilters, characterNameById]);

  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (typeof userFilters.minTokens === "number") count += 1;
    if (typeof userFilters.maxTokens === "number") count += 1;
    if (userFilters.fromDate) count += 1;
    if (userFilters.toDate) count += 1;
    if (typeof userFilters.maxConversationCount === "number") count += 1;
    if (typeof userFilters.minUniqueCharacters === "number") count += 1;
    if (userFilters.requiredCharacterIds?.length) count += 1;
    return count;
  }, [userFilters]);

  const fetchUsers = useCallback(
    async (options?: { hardRefresh?: boolean }) => {
      setLoading(true);
      setError(null);

      try {
        const response = await getUsersWithMessages({
          all: true,
          fromDate: userFilters.fromDate ?? undefined,
          toDate: userFilters.toDate ?? undefined,
          hardRefresh: options?.hardRefresh ? true : undefined,
        });

        setUsers(response?.data || []);
        setCurrentPage(1);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          "Failed to load users with messages.";
        setError(errorMessage);
        setUsers([]);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    },
    [userFilters.fromDate, userFilters.toDate]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const changePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const changeItemsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const setSortFilterType = (type: UsersWithMessagesSort) => {
    setFilterType(type);
    setCurrentPage(1);
  };

  const handleUserFilterToggle = (key: CampaignUserFilterKey) => {
    setUserFilters((current) => ({
      ...current,
      [key]: !current[key],
    }));
    setCurrentPage(1);
  };

  const handleUserAdvancedFiltersApply = (
    filters: Partial<CampaignUserFilters>
  ) => {
    setUserFilters((current) => ({ ...current, ...filters }));
    setCurrentPage(1);
    setIsAdvancedFilterModalOpen(false);
  };

  const handleAdvancedFiltersReset = () => {
    setUserFilters({
      sort: "latest",
      paidUsersOnly: false,
      ranOutOfTokensOnly: false,
      minTokens: null,
      maxTokens: null,
      fromDate: null,
      toDate: null,
      maxConversationCount: null,
      minUniqueCharacters: null,
      requiredCharacterIds: [],
    });
    setCurrentPage(1);
  };

  const handleViewProfileClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    userId?: string
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!userId) {
      return;
    }

    window.open(`/admin/user/${userId}`, "_blank");
  };

const totalTokensForUser = (user: MessageUser) => {
  if (typeof user.totalTokensSpent === "number") {
    return user.totalTokensSpent;
  }
  return (user.characters ?? []).reduce((sum: number, character) => {
    return sum + (character.tokens ?? 0);
  }, 0);
};

const isPaidMessageUser = (user: MessageUser) => {
  const highest = (user.highestBadge ?? "").trim().toLowerCase();
  if (highest && highest !== "subscriber") return true;
  const current = (user.membershipBadge ?? "").trim().toLowerCase();
  if (current && current !== "subscriber") return true;
  return false;
};

const filteredUsers = useMemo(() => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

    const filterByCharacter = (user: MessageUser) => {
      if (!userFilters.requiredCharacterIds?.length) return true;
      const ids = userFilters.requiredCharacterIds;
      return (user.characters ?? []).some(
        (character: NonNullable<MessageUser["characters"]>[number]) =>
          ids.includes(character.characterId)
      );
    };

    const userMessageCount = (user: MessageUser) => {
      const fromCharacters = (user.characters ?? []).reduce(
        (sum: number, character) => sum + (character.userMessageCount ?? 0),
        0
      );
      return fromCharacters;
    };

    const filtered = users
      .filter((user) => {
        if (userFilters.paidUsersOnly && !isPaidMessageUser(user)) return false;
        if (userFilters.ranOutOfTokensOnly) {
          const everRanOut =
            typeof user.everRanOutOfTokens !== "undefined"
              ? Boolean(user.everRanOutOfTokens)
              : false;
          if (!everRanOut) return false;
        }
        if (normalizedSearch) {
          const haystack = `${user.firstName ?? ""} ${user.lastName ?? ""} ${user.email ?? ""}`.toLowerCase();
          if (!haystack.includes(normalizedSearch)) return false;
        }

        if (!filterByCharacter(user)) return false;

        const tokens = totalTokensForUser(user);
        if (
          typeof userFilters.minTokens === "number" &&
          tokens < userFilters.minTokens
        )
          return false;
        if (
          typeof userFilters.maxTokens === "number" &&
          tokens > userFilters.maxTokens
        )
          return false;

        const messageCount = userMessageCount(user);
        if (
          typeof userFilters.maxConversationCount === "number" &&
          messageCount > userFilters.maxConversationCount
        )
          return false;

        if (
          typeof userFilters.minUniqueCharacters === "number" &&
          (user.characters?.length ?? 0) < userFilters.minUniqueCharacters
        )
          return false;

        return true;
      });

    const sorted = [...filtered].sort((a, b) => {
      if (filterType === "latest") {
        const aTime = a.latestMessageDate ? new Date(a.latestMessageDate).getTime() : 0;
        const bTime = b.latestMessageDate ? new Date(b.latestMessageDate).getTime() : 0;
        return bTime - aTime;
      }
      // "tokens" is repurposed to "most conversations" in UI, so sort by totalConversations desc
      const aConvos = typeof a.totalConversations === "number" ? a.totalConversations : 0;
      const bConvos = typeof b.totalConversations === "number" ? b.totalConversations : 0;
      if (bConvos !== aConvos) return bConvos - aConvos;

      // Tie-breaker: more recent activity first
      const aTime = a.latestMessageDate ? new Date(a.latestMessageDate).getTime() : 0;
      const bTime = b.latestMessageDate ? new Date(b.latestMessageDate).getTime() : 0;
      return bTime - aTime;
    });

    return sorted;
  }, [users, userFilters, filterType, searchTerm]);

  const visibleTotals = useMemo(() => {
    const totalUsers = filteredUsers.length;
    const totalConversations = filteredUsers.reduce(
      (sum, user) => sum + (user.totalConversations ?? 0),
      0
    );
    const activeToday = filteredUsers.filter((user) => {
      if (!user.latestMessageDate) return false;
      const diff =
        Date.now() - new Date(user.latestMessageDate).getTime();
      return diff >= 0 && diff <= 24 * 60 * 60 * 1000;
    }).length;
    const activeSubscribers = filteredUsers.filter((user) =>
      Boolean(user.isActivelyPaying)
    ).length;
    const averageConversations =
      totalUsers > 0 ? Math.round(totalConversations / totalUsers) : 0;

    return {
      totalUsers,
      totalConversations,
      activeToday,
      activeSubscribers,
      averageConversations,
    };
  }, [filteredUsers]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredUsers.length / itemsPerPage)
  );
  const showPagination = pageCount > 1;
  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);
  const totalUsersSummary = visibleTotals.totalUsers;
  const totalConversationsSummary = visibleTotals.totalConversations;
  const averageConversationsSummary = visibleTotals.averageConversations;
  const activeTodaySummary = visibleTotals.activeToday;
  const activeSubscribersSummary = visibleTotals.activeSubscribers;

  return (
    <>
      <div className="admin">
        <AdminHeader></AdminHeader>
        <div className="edit-character px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="title text-3xl font-semibold text-gray-900">
                Messages
              </div>
              <div className="sub-title mt-2 text-gray-500">
                Monitor user engagement and drill into individual conversations.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Users With Messages
              </div>
              <div className="mt-3 text-3xl font-semibold text-gray-900">
                {totalUsersSummary}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Currently visible after filters
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Total Conversations
              </div>
              <div className="mt-3 text-3xl font-semibold text-gray-900">
                {totalConversationsSummary}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Average {averageConversationsSummary} per user
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Active Today
              </div>
              <div className="mt-3 text-3xl font-semibold text-gray-900">
                {activeTodaySummary}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Sent a message in the last 24 hours
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Active Paid Subscribers
              </div>
              <div className="mt-3 text-3xl font-semibold text-gray-900">
                {activeSubscribersSummary}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Current paid users with active badges
              </div>
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="mt-8 bg-white rounded-2xl shadow p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex flex-col gap-3 w-full">
                <div className="relative w-full min-w-[320px] md:max-w-xl lg:max-w-2xl">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent transition-colors text-sm"
                  />
                  <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M9 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="m15.5 15.5-2.5-2.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </div>

                <div className="flex flex-wrap items-start gap-2">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortFilterType(option.value)}
                      className={`px-4 py-2 text-xs font-semibold rounded-full transition-colors border ${
                        filterType === option.value
                          ? "bg-[var(--primary-color)] text-white border-[var(--primary-color)] shadow-sm"
                          : "bg-gray-100 text-gray-700 border-gray-200 hover:text-[var(--primary-color)] hover:border-[var(--primary-color)]"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                  {FILTER_TOGGLES.map((toggle) => {
                    const isActive = userFilters[toggle.key];
                    return (
                      <button
                        key={toggle.key}
                        type="button"
                        onClick={() => handleUserFilterToggle(toggle.key)}
                        className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          isActive
                            ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white shadow-sm"
                            : "border-gray-200 bg-gray-100 text-gray-600 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                        }`}
                      >
                        {toggle.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setIsAdvancedFilterModalOpen(true)}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                      <svg
                        className="h-3.5 w-3.5"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.3 2.3a1.3 1.3 0 0 0-2.6 0l-.2 1A5.3 5.3 0 0 0 6.3 4l-.9-.5a1.3 1.3 0 0 0-1.8.5L2.8 6a1.3 1.3 0 0 0 .5 1.8l.9.5a5.3 5.3 0 0 0 0 2.1l-.9.5a1.3 1.3 0 0 0-.5 1.8l.8 1.4a1.3 1.3 0 0 0 1.8.5l.9-.5a5.3 5.3 0 0 0 2.2.7l.2 1a1.3 1.3 0 0 0 2.6 0l.2-1a5.3 5.3 0 0 0 2.2-.7l.9.5a1.3 1.3 0 0 0 1.8-.5l.8-1.4a1.3 1.3 0 0 0-.5-1.8l-.9-.5a5.3 5.3 0 0 0 0-2.1l.9.5a1.3 1.3 0 0 0 .5-1.8l-.8-1.4a1.3 1.3 0 0 0-1.8-.5l-.9.5a5.3 5.3 0 0 0-2.2-.7l-.2-1Z"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 12.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>Advanced filters</span>
                    {advancedFilterCount > 0 ? (
                      <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--primary-color)] px-2 text-[11px] font-semibold text-white">
                        {advancedFilterCount}
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => fetchUsers({ hardRefresh: true })}
                  disabled={loading}
                  className={`rounded-full px-4 py-2 text-xs font-semibold shadow-sm transition min-w-[120px] whitespace-nowrap ${
                    loading
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]/90"
                  }`}
                >
                  {loading ? "Refreshing…" : "Hard Refresh"}
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="mt-6">
            {loading ? (
              <div className="bg-white rounded-2xl shadow p-8 text-center text-gray-500">
                Loading users…
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl border border-red-200 p-8 text-center text-red-600">
                {error}
              </div>
            ) : filteredUsers.length ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {paginatedUsers.map((userItem) => {
                  const initials = `${
                    userItem.firstName?.charAt(0) || ""
                  }${userItem.lastName?.charAt(0) || ""}`.toUpperCase();
                  const latestDate = userItem.latestMessageDate
                    ? new Date(userItem.latestMessageDate)
                    : null;
                  const hasValidDate =
                    latestDate !== null && !Number.isNaN(latestDate.getTime());
                  const latestDateLabel = hasValidDate
                    ? latestDate.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "No messages";
                  const totalConversationsDisplay =
                    typeof userItem.totalConversations === "number"
                      ? userItem.totalConversations
                      : 0;
                  const {
                    label: membershipBadgeLabel,
                    className: membershipBadgeClassName,
                  } = getMembershipBadgeMeta(userItem.highestBadge);
                  const isActivelyPaying = Boolean(userItem.isActivelyPaying);
                  const paymentStatusLabel = isActivelyPaying
                    ? "Actively paying"
                    : "Not actively paying";
                  const paymentStatusClassName = isActivelyPaying
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-gray-300 bg-gray-100 text-gray-600";
                  const ranOutOfTokens = Boolean(userItem.ranOutOfTokens);
                  const tokenStatusLabel = ranOutOfTokens
                    ? "Ran out of tokens"
                    : "Tokens available";
                  const tokenStatusClassName = ranOutOfTokens
                    ? "border-rose-200 bg-rose-50 text-rose-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700";

                  return (
                    <div
                      key={userItem._id}
                      className="bg-gray-50 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-[var(--primary-color)]/30"
                    >
                      <div className="p-5 flex flex-col gap-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-[var(--primary-color)]/10 flex items-center justify-center text-[var(--primary-color)] font-semibold">
                              {initials || "?"}
                            </div>
                            <div>
                              <div className="text-base font-semibold text-gray-900">
                                {userItem.firstName} {userItem.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {userItem.email}
                              </div>
                              <div className="mt-2 flex flex-wrap items-start gap-2">
                                <span
                                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${membershipBadgeClassName}`}
                                >
                                  {membershipBadgeLabel}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${paymentStatusClassName}`}
                                >
                                  {paymentStatusLabel}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tokenStatusClassName}`}
                                >
                                  {tokenStatusLabel}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50">
                              ID {userItem._id?.slice(-6) || "—"}
                            </span>
                            <button
                              type="button"
                              onClick={(event) =>
                                handleViewProfileClick(event, userItem._id)
                              }
                              className="inline-flex items-center whitespace-nowrap rounded-full border border-[var(--primary-color)]/40 px-3 py-1 text-xs font-semibold text-[var(--primary-color)] bg-white hover:bg-[var(--primary-color)]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--primary-color)]"
                            >
                              View User Profile
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex gap-6">
                            <div>
                              <div className="text-xs uppercase tracking-wider text-gray-500">
                                Conversations
                              </div>
                              <div className="mt-1 text-lg font-semibold text-gray-900">
                                {totalConversationsDisplay}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-gray-500">
                                Last Message
                              </div>
                              <div className="mt-1 text-sm text-gray-700">
                                {latestDateLabel}
                              </div>
                            </div>
                          </div>
                          <Link
                            to={`/admin/messages/${userItem._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-[var(--primary-color)] hover:text-[var(--primary-color)]/80 inline-flex items-center gap-1"
                          >
                            View message history →
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center text-gray-500">
                No users match your current filters.
              </div>
            )}
          </div>

          {/* Pagination */}
          {showPagination && (
            <div className="flex justify-center mt-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-sm">
                <button
                  type="button"
                  onClick={() => changePage(Math.max(1, currentPage - 1))}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-[var(--primary-color)] disabled:text-gray-300"
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <div className="flex items-center gap-1">
                  {(() => {
                    const buttons = [];
                    const maxButtons = 7;
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(pageCount, start + maxButtons - 1);
                    const adjustedStart = Math.max(1, end - maxButtons + 1);

                    if (adjustedStart > 1) {
                      buttons.push(1, "...", adjustedStart);
                    }
                    for (let page = adjustedStart; page <= end; page += 1) {
                      buttons.push(page);
                    }
                    if (end < pageCount) {
                      buttons.push("...", pageCount);
                    }
                    return buttons;
                  })().map((value, index) => {
                    if (value === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 text-xs text-gray-400"
                        >
                          …
                        </span>
                      );
                    }
                    const pageNumber = value as number;
                    const isActive = currentPage === pageNumber;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => changePage(pageNumber)}
                        className={`px-3 py-1 text-sm font-semibold rounded-full transition-colors ${
                          isActive
                            ? "bg-[var(--primary-color)] text-white"
                            : "text-gray-700 hover:bg-[var(--secondary-color)]"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    changePage(Math.min(pageCount, currentPage + 1))
                  }
                  className="px-3 py-1 text-sm text-gray-600 hover:text-[var(--primary-color)] disabled:text-gray-300"
                  disabled={currentPage === pageCount}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
        <AdvancedFilterModal
          isOpen={isAdvancedFilterModalOpen}
          onClose={() => setIsAdvancedFilterModalOpen(false)}
          onApply={handleUserAdvancedFiltersApply}
          onReset={handleAdvancedFiltersReset}
          initialValues={{
            ...userFilters,
            minTokens: userFilters.minTokens ?? null,
            maxTokens: userFilters.maxTokens ?? null,
            fromDate: userFilters.fromDate ?? null,
            toDate: userFilters.toDate ?? null,
            maxConversationCount: userFilters.maxConversationCount ?? null,
            minUniqueCharacters: userFilters.minUniqueCharacters ?? null,
            requiredCharacterIds: userFilters.requiredCharacterIds ?? [],
          }}
          availableCharacters={characters}
          isLoading={loading}
        />
      </div>
    </>
  );
};

const Messages: React.FC = () => (
  <CharacterLibraryProvider>
    <MessagesFC />
  </CharacterLibraryProvider>
);

export default Messages;
